import React from 'react';

import { ISmartFloorPayloadUI } from '~/src/dash/apiV2/smartFloors/crud';
import { SmartFloorsAPIKey } from '~/src/dash/apiV2/smartFloors/crud';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import {
  ICustomRenderCellJSXProps,
  IListViewTableColumns,
  IListViewTableRow,
  Order,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { useListViewTableStore } from '~/src/dash/components/tables/ListViewTable/context';
import { CustomTextRender } from '~/src/dash/components/tables/ListViewTable/ListViewTableHead/templates';
import {
  CurrencyRender,
  EllipsisWithTooltipRender,
  HighlightedStatusTextRender,
  PercentTrendRender,
  PriceTextInputRender,
  TruncatedChipsRender,
} from '~/src/dash/components/tables/ListViewTable/ListViewTableRow/templates';
import { TextButton } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';

interface ISmartFloorsListViewTable {
  columns: IListViewTableColumns;
}

const SelectedSiteIDOnChartRender = (props: ICustomRenderCellJSXProps) => {
  const { customFunctions, customProps } = useListViewTableStore();
  const { row, value } = props;

  return (
    <TextButton
      onClick={() => customFunctions.handleSelectSiteOnChart(Number(row.ID.value), value)}
      size='small'
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 'fit-content',
      }}
    >
      <IconV2
        large
        name='eye'
        color={
          customProps.selectedSiteIDForChart === Number(row.ID.value)
            ? publicaColors.darkGray
            : publicaColors.lightGray
        }
      />
    </TextButton>
  );
};

export const formatSmartFloorsResponseIntoLVTRows = (
  smartFloors: ISmartFloorPayloadUI[],
  cfg: ISmartFloorsListViewTable,
) => {
  const formattedRows = [] as IListViewTableRow[];
  smartFloors.forEach((sf) => {
    const formattedRow = {} as IListViewTableRow;
    Object.values(cfg.columns).forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      // ensure value is correct
      let value: any = sf[responseKey as keyof ISmartFloorPayloadUI];
      if (isNumeric) {
        value = Number(value);
      }
      if (formatValue) {
        value = formatValue(value);
      }

      formattedRow[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
      };
    });
    formattedRows.push(formattedRow);
  });
  return formattedRows;
};

export interface ISmartFloorsListViewConfig {
  cypressClassName: string;
  defaultState: {
    activeColumnKeys?: SmartFloorsAPIKey[];
    isTableExpanded?: boolean;
    order: Order;
    orderBy: string;
    page: number;
    rowsPerPage?: RowsPerPage;
  };
  columnSelector: {
    resetKeys?: SmartFloorsAPIKey[];
  };
  isInitialized?: boolean; // keeps track of changes to config across pages
  trends: SmartFloorsAPIKey[];
  table: ISmartFloorsListViewTable;
}

export const smartFloorsListViewConfig = {
  cypressClassName: 'smartfloorslistview',
  defaultState: {
    activeColumnKeys: [
      'BaseBidFloor',
      'MinBidFloor',
      'MaxBidFloor',
      'TestIncrementHigh',
      'OptimizedBidFloor',
      'EstimatedRevenueUplift',
    ],
    isTableExpanded: false,
    order: 'desc',
    orderBy: 'ID',
    page: 0,
    rowsPerPage: 10,
  },
  columnSelector: {
    resetKeys: [
      'BaseBidFloor',
      'MinBidFloor',
      'MaxBidFloor',
      'TestIncrementHigh',
      'OptimizedBidFloor',
      'EstimatedRevenueUplift',
    ],
  },
  trends: ['OptimizedFloor12HourTrend', 'EstimatedRevenueUplift12HourTrend'],
  table: {
    columns: {
      CustomLeftColumn: {
        cellJSX: SelectedSiteIDOnChartRender,
        cellRenderType: 'simple_switch',
        disableSort: true,
        defaultOrderDirection: 'asc',
        key: 'CustomLeftColumn',
        label: 'Chart Display',
        requestKey: '',
        responseKey: 'isSiteIDSelected',
        valueType: 'boolean',
        cellStyle: { 'text-align': 'center', 'vertical-align': 'middle' },
        headerStyle: { 'text-align': 'center', 'min-width': '144px' },
        tKey: 'ChartDisplay',
      },
      ID: {
        formatValue: (value: number) => value.toString(),
        isHidden: true,
        isSearchable: true,
        key: 'ID',
        keyOverride: 'SiteID',
        label: 'ID',
        requestKey: '',
        responseKey: 'SiteID',
        valueType: 'string',
        tKey: 'ID',
      },
      Name: {
        // required Name field is used for channelName
        cellJSX: EllipsisWithTooltipRender,
        cellRenderType: 'name',
        defaultOrderDirection: 'asc',
        headerRenderType: 'name',
        isSearchable: true,
        key: 'Name',
        keyOverride: 'ChannelName',
        label: 'Channels',
        requestKey: '',
        responseKey: 'ChannelName',
        valueType: 'string',
        tKey: 'Channels',
      },
      BaseBidFloor: {
        cellJSX: PriceTextInputRender,
        cellRenderType: 'number',
        headerRenderType: 'number_select',
        headerTooltipText: 'set the starting floor for this site',
        headerTooltipTKey: 'BiddersTooltipSmartFloorsBaseFloor',
        isDefaultActive: true,
        key: 'BaseBidFloor',
        label: 'Base Floor',
        requestKey: '',
        responseKey: 'BaseBidFloor',
        valueType: 'number',
        tKey: 'BaseFloor',
      },
      MinBidFloor: {
        cellJSX: CurrencyRender,
        cellRenderType: 'number',
        headerRenderType: 'number',
        key: 'MinBidFloor',
        label: 'Min Bid Floor',
        requestKey: '',
        responseKey: 'MinBidFloor',
        valueType: 'number',
        tKey: 'MinBidFloor',
      },
      MaxBidFloor: {
        cellJSX: CurrencyRender,
        cellRenderType: 'number',
        headerRenderType: 'number',
        key: 'MaxBidFloor',
        label: 'Max Bid Floor',
        requestKey: '',
        responseKey: 'MaxBidFloor',
        valueType: 'number',
        tKey: 'MaxBidFloor',
      },
      TestIncrementHigh: {
        cellJSX: CurrencyRender,
        cellRenderType: 'number',
        key: 'TestIncrementHigh', // high and low are set to same value
        label: 'Test Increment',
        requestKey: '',
        responseKey: 'TestIncrementHigh',
        valueType: 'number',
        headerRenderType: 'number',
        tKey: 'TestIncrement',
      },
      OptimizedBidFloor: {
        cellJSX: CurrencyRender,
        key: 'OptimizedBidFloor',
        label: 'Optimized Floor',
        requestKey: '',
        responseKey: 'OptimizedBidFloor',
        valueType: 'number',
        headerRenderType: 'number',
        headerJSX: CustomTextRender,
        headerJSXStyle: {
          backgroundColor: publicaColors.kojiOrange,
          fontWeight: 500,
          borderRadius: '2px',
        },
        cellRenderType: 'number',
        tKey: 'OptimizedFloor',
      },
      OptimizedBidFloor12HourTrend: {
        cellJSX: PercentTrendRender,
        cellRenderType: 'number',
        defaultOrderDirection: 'desc',
        headerRenderType: 'number',
        headerTooltipText: 'Optimized Floor Trend L12H Compared to Previous Period',
        headerTooltipTKey: 'BiddersTooltipSmartFloorsOptimizedFloorTrend',
        headerJSX: CustomTextRender,
        headerJSXStyle: {
          backgroundColor: publicaColors.lightGray,
          fontWeight: 500,
          borderRadius: '2px',
        },
        isDefaultActive: false,
        isNumeric: true,
        isSuperUserOnly: true,
        key: 'OptimizedBidFloor12HourTrend',
        label: '% L12H Floor',
        trendKey: 'OptimizedBidFloor12HourTrend',
        requestKey: '',
        responseKey: 'OptimizedBidFloor12HourTrend',
        valueType: 'number',
        tKey: 'PercentL12HFloor',
      },
      EstimatedRevenueUplift: {
        cellJSX: CurrencyRender,
        cellRenderType: 'number',
        defaultOrderDirection: 'desc',
        headerRenderType: 'number',
        headerTooltipText: 'Estimated Revenue Uplift for the last 12 hours',
        headerTooltipTKey: 'BiddersTooltipSmartFloorsEstimatedUplift',
        headerJSX: CustomTextRender,
        headerJSXStyle: {
          backgroundColor: publicaColors.eyeBlue,
          fontWeight: 500,
          borderRadius: '2px',
          minWidth: '142px',
        },
        isDefaultActive: true,
        isNumeric: true,
        key: 'EstimatedRevenueUplift',
        label: 'L12H Estimated Uplift',
        requestKey: '',
        responseKey: 'EstimatedRevenueUplift',
        trendKey: 'EstimatedRevenueUplift',
        valueType: 'number',
        tKey: 'L12HEstimatedUplift',
      },
      EstimatedRevenueUplift12HourTrend: {
        cellJSX: PercentTrendRender,
        cellRenderType: 'number',
        defaultOrderDirection: 'desc',
        headerRenderType: 'number',
        headerTooltipText: 'Estimated Uplift Trend L12H Compared to Previous Period',
        headerTooltipTKey: 'BiddersTooltipSmartFloorsEstimatedUpliftTrend',
        headerJSX: CustomTextRender,
        headerJSXStyle: {
          backgroundColor: publicaColors.lightGray,
          fontWeight: 500,
          borderRadius: '2px',
        },
        isDefaultActive: false,
        isSuperUserOnly: true,
        key: 'EstimatedRevenueUplift12HourTrend',
        label: '% L12H Uplift',
        requestKey: '',
        responseKey: 'EstimatedRevenueUplift12HourTrend',
        valueType: 'number',
        tKey: 'PercentL12HUplift',
      },
      ChannelGroupNames: {
        cellJSX: TruncatedChipsRender,
        cellRenderType: 'string',
        defaultOrderDirection: 'asc',
        isSearchable: true,
        key: 'ChannelGroupNames',
        label: 'Channel Groups',
        requestKey: '',
        responseKey: 'ChannelGroupNames',
        valueType: 'array',
        tKey: 'ChannelGroups',
      },
      Status2: {
        cellJSX: HighlightedStatusTextRender,
        headerRenderType: 'centered_text',
        headerTooltipText:
          'Running: running with currently set base floor. Pending: waiting to start with newly set base floor.',
        headerTooltipTKey: 'BiddersTooltipSmartFloorsStatus',
        key: 'Status2',
        label: 'Status',
        requestKey: '',
        responseKey: 'Status',
        valueType: 'string',
        tKey: 'Status',
      },
    },
  },
} as ISmartFloorsListViewConfig;

// this is an almost identical config to smartFloorsListViewConfig with some columns removed
// we created a separate config to better handle the differences in the UI and to avoid confusion
export const smartFloorsListViewAdaptiveConfig = {
  cypressClassName: 'smartfloorslistview',
  defaultState: {
    activeColumnKeys: [
      'BaseBidFloor',
      'MinBidFloor',
      'MaxBidFloor',
      'TestIncrementHigh',
      'EstimatedRevenueUplift',
    ],
    isTableExpanded: false,
    order: 'desc',
    orderBy: 'ID',
    page: 0,
    rowsPerPage: 10,
  },
  columnSelector: {
    resetKeys: [
      'BaseBidFloor',
      'MinBidFloor',
      'MaxBidFloor',
      'TestIncrementHigh',
      'EstimatedRevenueUplift',
    ],
  },
  trends: ['OptimizedFloor12HourTrend', 'EstimatedRevenueUplift12HourTrend'],
  table: {
    columns: {
      CustomLeftColumn: {
        cellJSX: SelectedSiteIDOnChartRender,
        cellRenderType: 'simple_switch',
        disableSort: true,
        defaultOrderDirection: 'asc',
        key: 'CustomLeftColumn',
        label: 'Chart Display',
        requestKey: '',
        responseKey: 'isSiteIDSelected',
        valueType: 'boolean',
        cellStyle: { 'text-align': 'center', 'vertical-align': 'middle' },
        headerStyle: { 'text-align': 'center', 'min-width': '144px' },
        tKey: 'ChartDisplay',
      },
      ID: {
        formatValue: (value: number) => value.toString(),
        isHidden: true,
        isSearchable: true,
        key: 'ID',
        keyOverride: 'SiteID',
        label: 'ID',
        requestKey: '',
        responseKey: 'SiteID',
        valueType: 'string',
        tKey: 'ID',
      },
      Name: {
        // required Name field is used for channelName
        cellJSX: EllipsisWithTooltipRender,
        cellRenderType: 'name',
        defaultOrderDirection: 'asc',
        headerRenderType: 'name',
        isSearchable: true,
        key: 'Name',
        keyOverride: 'ChannelName',
        label: 'Channels',
        requestKey: '',
        responseKey: 'ChannelName',
        valueType: 'string',
        tKey: 'Channels',
      },
      BaseBidFloor: {
        cellJSX: PriceTextInputRender,
        cellRenderType: 'number',
        headerRenderType: 'number_select',
        headerTooltipText: 'set the starting floor for this site',
        headerTooltipTKey: 'BiddersTooltipSmartFloorsBaseFloor',
        isDefaultActive: true,
        key: 'BaseBidFloor',
        label: 'Base Floor',
        requestKey: '',
        responseKey: 'BaseBidFloor',
        valueType: 'number',
        tKey: 'BaseFloor',
      },
      MinBidFloor: {
        cellJSX: CurrencyRender,
        cellRenderType: 'number',
        headerRenderType: 'number',
        key: 'MinBidFloor',
        label: 'Min Bid Floor',
        requestKey: '',
        responseKey: 'MinBidFloor',
        valueType: 'number',
        tKey: 'MinBidFloor',
      },
      MaxBidFloor: {
        cellJSX: CurrencyRender,
        cellRenderType: 'number',
        headerRenderType: 'number',
        key: 'MaxBidFloor',
        label: 'Max Bid Floor',
        requestKey: '',
        responseKey: 'MaxBidFloor',
        valueType: 'number',
        tKey: 'MaxBidFloor',
      },
      TestIncrementHigh: {
        cellJSX: CurrencyRender,
        cellRenderType: 'number',
        key: 'TestIncrementHigh', // high and low are set to same value
        label: 'Test Increment',
        requestKey: '',
        responseKey: 'TestIncrementHigh',
        valueType: 'number',
        headerRenderType: 'number',
        tKey: 'TestIncrement',
      },
      ChannelGroupNames: {
        cellJSX: TruncatedChipsRender,
        cellRenderType: 'string',
        defaultOrderDirection: 'asc',
        isSearchable: true,
        key: 'ChannelGroupNames',
        label: 'Channel Groups',
        requestKey: '',
        responseKey: 'ChannelGroupNames',
        valueType: 'array',
        tKey: 'ChannelGroups',
      },
      Status2: {
        cellJSX: HighlightedStatusTextRender,
        headerRenderType: 'centered_text',
        headerTooltipText:
          'Running: running with currently set base floor. Pending: waiting to start with newly set base floor.',
        headerTooltipTKey: 'BiddersTooltipSmartFloorsStatus',
        key: 'Status2',
        label: 'Status',
        requestKey: '',
        responseKey: 'Status',
        valueType: 'string',
        tKey: 'Status',
      },
    },
  },
} as ISmartFloorsListViewConfig;
